import { useState } from "react";
import { useNavigate } from "@tanstack/react-router";
import { cn } from "@/lib/utils";
import {
  CATEGORY_LABELS, CATEGORY_VALUES,
  PAYMENT_METHOD_LABELS,
  type Expense, type ExpenseCreate,
  type ExpenseCategory, type ExpensePaymentMethod,
} from "@cold-crew/shared";

interface GastoFormProps {
  defaultValues?: Partial<Expense>;
  onSubmit:       (data: ExpenseCreate) => Promise<void>;
  isLoading?:     boolean;
  submitLabel?:   string;
}

const inputCls = cn(
  "h-11 w-full rounded-xl border border-[#ddd8cf] bg-[#fbfaf7] px-3.5 text-[13px] text-[#282622]",
  "placeholder:text-[#aaa59c] focus:border-[#f47b2a] focus:outline-none focus:ring-2 focus:ring-[#f47b2a]/15",
  "transition-colors disabled:opacity-60"
);
const labelCls = "mb-1.5 block text-[12px] font-bold text-[#3d3934]";
const fieldCls = "space-y-1.5";

const today = () => new Date().toISOString().slice(0, 10);

export function GastoForm({
  defaultValues, onSubmit, isLoading, submitLabel = "Guardar gasto",
}: GastoFormProps) {
  const navigate = useNavigate();

  const [fecha,       setFecha]      = useState(defaultValues?.fecha       ?? today());
  const [categoria,   setCategoria]  = useState<ExpenseCategory>(defaultValues?.categoria ?? "cafe");
  const [descripcion, setDescripcion]= useState(defaultValues?.descripcion ?? "");
  const [monto,       setMonto]      = useState(String(defaultValues?.monto ?? ""));
  const [metodo_pago, setMetodoPago] = useState<ExpensePaymentMethod>(defaultValues?.metodo_pago ?? "efectivo");
  const [proveedor,   setProveedor]  = useState(defaultValues?.proveedor   ?? "");
  const [comprobante, setComprobante]= useState(defaultValues?.comprobante ?? "");
  const [notas,       setNotas]      = useState(defaultValues?.notas       ?? "");
  const [error,       setError]      = useState<string | null>(null);

  const requiresDescription = categoria === "otro";

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);

    const montoNum = parseFloat(monto);
    if (!monto || isNaN(montoNum) || montoNum <= 0) {
      setError("El monto debe ser mayor a cero.");
      return;
    }
    if (requiresDescription && !descripcion.trim()) {
      setError("La descripción es obligatoria cuando la categoría es 'Otro'.");
      return;
    }

    try {
      await onSubmit({
        fecha,
        categoria,
        descripcion: descripcion.trim(),
        monto:       montoNum,
        metodo_pago,
        proveedor:   proveedor.trim()   || undefined,
        comprobante: comprobante.trim() || undefined,
        notas:       notas.trim()       || undefined,
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : "Error al guardar el gasto.");
    }
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-5">

      {/* Fecha + Categoría */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div className={fieldCls}>
          <label className={labelCls}>Fecha *</label>
          <input type="date" value={fecha}
            onChange={(e) => setFecha(e.target.value)}
            disabled={isLoading} className={inputCls} required />
        </div>
        <div className={fieldCls}>
          <label className={labelCls}>Categoría *</label>
          <select value={categoria}
            onChange={(e) => setCategoria(e.target.value as ExpenseCategory)}
            disabled={isLoading} className={cn(inputCls, "cursor-pointer")}>
            {CATEGORY_VALUES.map((cat) => (
              <option key={cat} value={cat}>{CATEGORY_LABELS[cat]}</option>
            ))}
          </select>
        </div>
      </div>

      {/* Descripción — obligatoria si categoría = otro */}
      <div className={fieldCls}>
        <label className={labelCls}>
          Descripción {requiresDescription ? "*" : <span className="text-muted-foreground font-normal">(opcional)</span>}
        </label>
        <input
          type="text"
          placeholder={requiresDescription ? "Describe el gasto (obligatorio para 'Otro')" : "Descripción del gasto…"}
          value={descripcion}
          onChange={(e) => setDescripcion(e.target.value)}
          disabled={isLoading}
          required={requiresDescription}
          className={inputCls}
        />
        {requiresDescription && (
          <p className="text-xs text-amber-700 dark:text-amber-400">
            La categoría "Otro" requiere una descripción para identificar el gasto.
          </p>
        )}
      </div>

      {/* Monto + Método de pago */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div className={fieldCls}>
          <label className={labelCls}>Monto ($) *</label>
          <input type="number" min={0.01} step="0.01" placeholder="0.00"
            value={monto} onChange={(e) => setMonto(e.target.value)}
            disabled={isLoading} required className={inputCls} />
        </div>
        <div className={fieldCls}>
          <label className={labelCls}>Método de pago *</label>
          <select value={metodo_pago}
            onChange={(e) => setMetodoPago(e.target.value as ExpensePaymentMethod)}
            disabled={isLoading} className={cn(inputCls, "cursor-pointer")}>
            {(Object.entries(PAYMENT_METHOD_LABELS) as [ExpensePaymentMethod, string][]).map(([v, l]) => (
              <option key={v} value={v}>{l}</option>
            ))}
          </select>
        </div>
      </div>

      {/* Proveedor + Comprobante */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div className={fieldCls}>
          <label className={labelCls}>Proveedor <span className="text-muted-foreground font-normal">(opcional)</span></label>
          <input type="text" placeholder="Nombre del proveedor"
            value={proveedor} onChange={(e) => setProveedor(e.target.value)}
            disabled={isLoading} className={inputCls} />
        </div>
        <div className={fieldCls}>
          <label className={labelCls}>Comprobante / Folio <span className="text-muted-foreground font-normal">(opcional)</span></label>
          <input type="text" placeholder="No. de factura o recibo"
            value={comprobante} onChange={(e) => setComprobante(e.target.value)}
            disabled={isLoading} className={inputCls} />
        </div>
      </div>

      {/* Notas */}
      <div className={fieldCls}>
        <label className={labelCls}>Notas <span className="text-muted-foreground font-normal">(opcional)</span></label>
        <input type="text" placeholder="Observaciones adicionales"
          value={notas} onChange={(e) => setNotas(e.target.value)}
          disabled={isLoading} className={inputCls} />
      </div>

      {/* Error */}
      {error && (
        <p className="rounded-xl border border-red-200 bg-red-50 px-3 py-2.5 text-sm font-medium text-red-700">{error}</p>
      )}

      {/* Actions */}
      <div className="flex items-center justify-end gap-3 pt-1">
        <button type="button" onClick={() => navigate({ to: "/gastos" })}
          disabled={isLoading}
          className="h-11 rounded-xl border border-[#ddd8cf] bg-white px-5 text-sm font-semibold text-[#615d56] transition-colors hover:bg-[#f8f6f1] hover:text-[#171614] disabled:opacity-50">
          Cancelar
        </button>
        <button type="submit" disabled={isLoading}
          style={{ background: "#090909" }}
          className="flex h-11 items-center gap-2 rounded-xl px-5 text-sm font-bold text-white transition-opacity hover:opacity-90 disabled:opacity-60">
          {isLoading && (
            <span className="size-3.5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
          )}
          {submitLabel}
        </button>
      </div>
    </form>
  );
}
